import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, X, Check } from 'lucide-react';
import { BACKEND_BASE_URL } from '../config/api';
import { getActivePaymentSettingForUser } from '../ServiceAPI/application/api';
//import paymentQrImage from '../assets/payment-qr.jpeg';

const PaymentModal = ({ isOpen, verifiedApplications = [], onClose, isDark = false }) => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [paymentSetting, setPaymentSetting] = useState(null);
  //const [qrLoadError, setQrLoadError] = useState(false);

  // Initialize all applications as selected by default
  React.useEffect(() => {
    if (verifiedApplications.length > 0) {
      setSelectedApplications(verifiedApplications.map(app => app.ApplicationId));
    }
  }, [verifiedApplications]);

  React.useEffect(() => {
    const loadPaymentSetting = async () => {
      if (!isOpen) return;
      try {
        const response = await getActivePaymentSettingForUser();
        setPaymentSetting(response?.data || null);
      } catch (_err) {
        setPaymentSetting(null);
      }
    };

    loadPaymentSetting();
  }, [isOpen]);

  const handleApplicationToggle = (applicationId) => {
    setSelectedApplications(prev => {
      if (prev.includes(applicationId)) {
        return prev.filter(id => id !== applicationId);
      } else {
        return [...prev, applicationId];
      }
    });
  };

  // const getQrUrl = (qrPath) => {
  //   if (!qrPath) return paymentQrImage;
  //   const normalized = String(qrPath).replace(/\\/g, '/');
  //   if (/^https?:\/\//i.test(normalized)) return normalized;
  //   const marker = '/uploads/';
  //   const idx = normalized.toLowerCase().indexOf(marker);
  //   if (idx < 0) return paymentQrImage;
  //   const relative = normalized.slice(idx + 1);
  //   return `${BACKEND_BASE_URL}/${relative}`;
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (selectedApplications.length === 0) {
      setError('Please select at least one application to make payment for.');
      return;
    }

    setLoading(true);

    try {
      onClose?.();
      navigate(`/payment-form?ids=${selectedApplications.join(',')}`);
    } catch (err) {
      setError(err.message || "Failed to submit payment information");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className={`${isDark ? 'bg-gray-900' : 'bg-white'} rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.3)] max-w-[400px] w-full overflow-hidden relative border ${isDark ? 'border-gray-800' : 'border-gray-100'} animate-in fade-in zoom-in duration-300`}>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-6 top-6 z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Header - Compact & Premium */}
        <div className="pt-10 pb-6 px-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500 blur-2xl opacity-20 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-4 shadow-lg">
                <CreditCard size={32} className="text-white" />
              </div>
            </div>
          </div>
          <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Payment Required
          </h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Complete your application process
          </p>
        </div>

        {/* Body */}
        <div className="px-8 pb-10">
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Service Applications List - Compact */}
            <div className={`rounded-3xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'} p-1`}>
              <div className="max-h-40 overflow-y-auto custom-scrollbar px-2 py-2">
                {verifiedApplications.map((app) => (
                  <div
                    key={app.ApplicationId}
                    className={`group relative flex items-center p-3 rounded-2xl transition-all mb-1 last:mb-0 ${selectedApplications.includes(app.ApplicationId)
                      ? (isDark ? 'bg-blue-900/20' : 'bg-blue-50')
                      : 'transparent'
                      }`}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          id={`app-${app.ApplicationId}`}
                          checked={selectedApplications.includes(app.ApplicationId)}
                          onChange={() => handleApplicationToggle(app.ApplicationId)}
                          className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-gray-300 transition-all checked:border-blue-600 checked:bg-blue-600 focus:outline-none dark:border-gray-600"
                        />
                        <Check
                          size={12}
                          className="absolute left-[4px] top-[4px] text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"
                        />
                      </div>
                      <label htmlFor={`app-${app.ApplicationId}`} className="flex-1 cursor-pointer">
                        <div className={`text-sm font-semibold transition-colors ${selectedApplications.includes(app.ApplicationId)
                          ? (isDark ? 'text-blue-400' : 'text-blue-700')
                          : (isDark ? 'text-gray-200' : 'text-gray-700')
                          }`}>
                          {app.ServiceName}
                        </div>
                        <div className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {app.ServiceType}
                        </div>
                      </label>
                    </div>
                    <div className={`px-2 py-1 rounded-lg text-[10px] font-bold ${isDark ? 'bg-green-500/10 text-green-400' : 'bg-green-100 text-green-700'
                      }`}>
                      VERIFIED
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {paymentSetting && (
              <div className={`rounded-2xl p-3 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-100'}`}>
                <p className={`text-xs font-bold mb-1 ${isDark ? 'text-gray-200' : 'text-blue-900'}`}>
                  Pay To: {paymentSetting.Account_name || 'RTO Bharat'}
                </p>
                {paymentSetting.Upi_id && (
                  <p className={`text-xs font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                    UPI: {paymentSetting.Upi_id}
                  </p>
                )}
                {/* {(paymentSetting.Qr_image || paymentQrImage) && (
                  <img
                    src={getQrUrl(paymentSetting.Qr_image)}
                    alt="Payment QR"
                    className="mt-2 w-28 h-28 object-cover rounded-lg border border-blue-200"
                    onError={() => setQrLoadError(true)}
                  />
                )} */}
                {/* {qrLoadError && (
                  <p className={`mt-2 text-[11px] ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                    QR failed to load ({paymentSetting.Qr_image})
                  </p>
                )} */}
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-400 rounded-2xl text-[11px] font-medium animate-shake">
                {error}
              </div>
            )}

            {/* Submit Button - PhonePe style */}
            <button
              type="submit"
              disabled={loading || selectedApplications.length === 0}
              className={`w-full relative group overflow-hidden flex items-center justify-center p-4 rounded-2xl font-bold text-white transition-all transform active:scale-95 ${loading || selectedApplications.length === 0
                ? 'bg-gray-300 dark:bg-gray-800 cursor-not-allowed text-gray-500'
                : 'bg-blue-600 hover:bg-blue-700 shadow-[0_10px_30px_-10px_rgba(37,99,235,0.5)]'
                }`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span>Continue to Payment Form</span>
                </div>
              )}
            </button>
          </form>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 10px;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.2s cubic-bezier(.36,.07,.19,.97) both;
        }
      `}} />
    </div>

  );
};

export default PaymentModal;
